import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

/**
 * TemplateDesigner – v2.0
 * -------------------------------------------------
 * • Drag‑and‑drop PDF field layout with optional 5 px grid‑snap
 * • 1 px inner padding so field boxes never touch the canvas border
 * • Re‑usable <Header /> component keeps nav consistent across app
 * • LocalStorage autosave in case user refreshes mid‑editing
 * -------------------------------------------------
 */

// ──────────────────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────────────────
const DEFAULT_CANVAS = { width: 695, height: 842, label: "A4" }; // 72 DPI @ ≈ A4
const A5_CANVAS = { width: 620, height: 595, label: "A5" };
const DEMO_FIELDS = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
];
const FONT_FAMILIES = [
  "Inter",
  "Arial",
  "Times New Roman",
  "Georgia",
  "Courier New",
  "Verdana",
  "Tahoma",
];
const GRID_SIZE = 5; // px – used when snapToGrid enabled

// Util
const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
const snap = (v) => Math.round(v / GRID_SIZE) * GRID_SIZE;

// ──────────────────────────────────────────────────────────────────────────────
// Header component (shared)
// ──────────────────────────────────────────────────────────────────────────────
const Header = () => (
  <nav className="bg-white/95 backdrop-blur-md shadow sticky top-0 z-50 border-b">
    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
      <Link
        to="/"
        className="text-2xl font-extrabold bg-gradient-to-r from-slate-800 via-amber-600 to-stone-700 bg-clip-text text-transparent"
      >
        📄 Letterly
      </Link>
      <div className="hidden md:flex gap-6 text-slate-600 font-medium">
        <Link to="/dashboard" className="hover:text-amber-600">
          Dashboard
        </Link>
        <Link to="/templates" className="hover:text-amber-600">
          Templates
        </Link>
        <Link to="/jobs" className="hover:text-amber-600">
          PDF Jobs
        </Link>
        <Link to="/upload" className="hover:text-amber-600">
          Upload
        </Link>
      </div>
    </div>
  </nav>
);

// ──────────────────────────────────────────────────────────────────────────────
export default function TemplateDesigner() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const canvasRef = useRef(null);

  // Incoming data -------------------------------------------------------------
  const mapping = state?.mapping || {};
  const sampleData = state?.sampleData || [];
  const systemFields = state?.systemFields || [];
  const fileName = state?.fileName || "Untitled";

  const fieldPalette =
    systemFields.length && Object.keys(mapping).length
      ? systemFields.filter((f) => mapping[f.key])
      : DEMO_FIELDS;

  // Local state ---------------------------------------------------------------
  const [canvas, setCanvas] = useState(DEFAULT_CANVAS);
  const [fields, setFields] = useState(() =>
    JSON.parse(localStorage.getItem("letterly:tmpLayout") ?? "[]")
  );
  const [selectedIdx, setSelIdx] = useState(null);
  const [paletteDrag, setDrag] = useState(null);
  const [draggingIdx, setDIdx] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [snapToGrid, setSnap] = useState(true);

  // Autosave on change --------------------------------------------------------
  useEffect(() => {
    localStorage.setItem("letterly:tmpLayout", JSON.stringify(fields));
  }, [fields]);

  // Palette → canvas drop -----------------------------------------------------
  const handleDrop = (e) => {
    e.preventDefault();
    if (!paletteDrag || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let x = clamp(e.clientX - rect.left - 50, 1, canvas.width - 81);
    let y = clamp(e.clientY - rect.top - 10, 1, canvas.height - 31);
    if (snapToGrid) {
      x = snap(x);
      y = snap(y);
    }
    const value =
      sampleData[0]?.[mapping[paletteDrag.key]] || paletteDrag.label;
    setFields((f) => [
      ...f,
      {
        ...paletteDrag,
        x,
        y,
        fontSize: 14,
        align: "left",
        color: "#1e293b",
        bgColor: "#fffbe9",
        fontFamily: "Inter",
        value,
      },
    ]);
    setDrag(null);
  };

  const moveField = useCallback(
    (idx, e) => {
      if (draggingIdx === null || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      let x = clamp(
        e.clientX - rect.left - dragOffset.current.x,
        1,
        canvas.width - 81
      );
      let y = clamp(
        e.clientY - rect.top - dragOffset.current.y,
        1,
        canvas.height - 31
      );
      if (snapToGrid) {
        x = snap(x);
        y = snap(y);
      }
      setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, x, y } : f)));
    },
    [draggingIdx, canvas, snapToGrid]
  );

  // Global listeners ----------------------------------------------------------
  useEffect(() => {
    const onMove = (e) => moveField(draggingIdx, e);
    if (draggingIdx !== null) {
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", () => setDIdx(null), { once: true });
    }
    return () => window.removeEventListener("mousemove", onMove);
  }, [draggingIdx, moveField]);

  // Helpers -------------------------------------------------------------------
  const updateField = (i, obj) =>
    setFields((f) =>
      f.map((fld, idx) => (idx === i ? { ...fld, ...obj } : fld))
    );
  const removeField = (i) => setFields((f) => f.filter((_, idx) => idx !== i));

  // Start dragging a field
  const startDrag = (idx, e) => {
    e.stopPropagation();
    setDIdx(idx);
    const rect = e.target.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  // UI ------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-stone-50 to-amber-50"
      onMouseUp={() => setDIdx(null)}
    >
      <Header />

      {/* Heading */}
      <div className="text-center py-6">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
          Visual Template Designer
        </h1>
        <p className="text-slate-600">
          Drag & drop fields – snap‑to‑grid {snapToGrid ? "ON" : "OFF"}
        </p>
      </div>

      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 px-4 pb-12">
        {/* Palette */}
        <aside className="md:w-60 bg-white/90 border rounded-lg shadow p-4 h-max">
          <h2 className="font-semibold text-slate-700 mb-3">Fields</h2>
          {fieldPalette.map((f) => (
            <div
              key={f.key}
              draggable
              onDragStart={() => setDrag(f)}
              onDragEnd={() => setDrag(null)}
              className="mb-2 px-3 py-2 text-sm bg-amber-50 border cursor-grab rounded shadow hover:bg-amber-100"
            >
              {f.label}
            </div>
          ))}
          <hr className="my-3" />
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={snapToGrid}
              onChange={(e) => setSnap(e.target.checked)}
            />{" "}
            Snap to 5 px
          </label>
        </aside>

        {/* Canvas + controls */}
        <main className="flex-1">
          {/* Field controls */}
          {selectedIdx !== null && fields[selectedIdx] && (
            <div className="bg-white shadow rounded p-4 mb-4 flex flex-wrap gap-4 items-center">
              <label className="text-sm">
                Font&nbsp;Size{" "}
                <input
                  type="number"
                  min={8}
                  max={48}
                  value={fields[selectedIdx].fontSize}
                  onChange={(e) =>
                    updateField(selectedIdx, { fontSize: +e.target.value })
                  }
                  className="ml-1 border px-1 w-16"
                />
              </label>
              <label className="text-sm">
                Align{" "}
                <select
                  value={fields[selectedIdx].align}
                  onChange={(e) =>
                    updateField(selectedIdx, { align: e.target.value })
                  }
                  className="ml-1 border px-1"
                >
                  <option>left</option>
                  <option>center</option>
                  <option>right</option>
                </select>
              </label>
              <label className="text-sm">
                Color{" "}
                <input
                  type="color"
                  value={fields[selectedIdx].color}
                  onChange={(e) =>
                    updateField(selectedIdx, { color: e.target.value })
                  }
                  className="ml-1 w-6 h-6"
                />
              </label>
              <label className="text-sm">
                BG{" "}
                <input
                  type="color"
                  value={fields[selectedIdx].bgColor}
                  onChange={(e) =>
                    updateField(selectedIdx, { bgColor: e.target.value })
                  }
                  className="ml-1 w-6 h-6"
                />
              </label>
              <label className="text-sm">
                Font{" "}
                <select
                  value={fields[selectedIdx].fontFamily}
                  onChange={(e) =>
                    updateField(selectedIdx, { fontFamily: e.target.value })
                  }
                  className="ml-1 border px-1"
                >
                  {FONT_FAMILIES.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </label>
              <button
                onClick={() => setSelIdx(null)}
                className="ml-auto text-xs text-blue-600"
              >
                Done
              </button>
              <button
                onClick={() => {
                  removeField(selectedIdx);
                  setSelIdx(null);
                }}
                className="text-xs text-red-600"
              >
                Delete
              </button>
            </div>
          )}

          {/* Canvas */}
          <div
            ref={canvasRef}
            className="relative border-2 border-dashed border-amber-300 rounded-lg bg-white shadow"
            style={{ width: canvas.width, height: canvas.height }}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onMouseDown={() => setSelIdx(null)}
          >
            {fields.length === 0 && (
              <span className="absolute inset-0 flex items-center justify-center text-slate-400">
                Drag fields here
              </span>
            )}
            {fields.map((f, i) => (
              <div
                key={i}
                className={`absolute px-2 py-1 border border-amber-300 bg-amber-50 rounded cursor-move ${
                  selectedIdx === i ? "ring-2 ring-amber-500" : ""
                }`}
                style={{
                  left: f.x,
                  top: f.y,
                  fontSize: f.fontSize,
                  textAlign: f.align,
                  color: f.color,
                  background: f.bgColor,
                  fontFamily: f.fontFamily,
                  minWidth: 80,
                }}
                onMouseDown={(e) => {
                  startDrag(i, e);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelIdx(i);
                }}
              >
                <input
                  value={f.value}
                  onChange={(e) => updateField(i, { value: e.target.value })}
                  className="bg-transparent outline-none w-36"
                />
              </div>
            ))}
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={() => alert("Template saved!")}
              className="px-6 py-2 rounded bg-blue-700 text-white hover:bg-blue-800 shadow"
            >
              Save Template
            </button>
            <button
              onClick={() =>
                navigate("/preview", {
                  state: {
                    fields,
                    canvasSize: canvas,
                    sampleData,
                    mapping,
                    fileName,
                  },
                })
              }
              className="px-6 py-2 rounded bg-amber-600 text-white hover:bg-amber-700 shadow"
            >
              Preview
            </button>
            <select
              value={canvas.label}
              onChange={(e) =>
                setCanvas(e.target.value === "A4" ? DEFAULT_CANVAS : A5_CANVAS)
              }
              className="ml-auto border px-2 py-1 rounded text-sm"
            >
              <option value="A4">A4</option>
              <option value="A5">A5</option>
            </select>
          </div>
        </main>
      </div>
    </div>
  );
}
